/**
 * ChargingScheduleChart - Gantt-style charger × vehicle charging schedule
 *
 * Shows which vehicle is on which charger and when, with SOC progression
 * and charge rate info. Blocks turn red when vehicles won't have enough
 * charge to complete their routes before departure.
 */

import React, { useMemo } from 'react';
import { Box, Typography, Tooltip, alpha } from '@mui/material';
import {
    EvStation as ChargerIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import { useThemeMode, SectionCard } from '@vortx/shared-ui';

// Vehicle colors — same palette as SOCTrajectoryChart
const VEHICLE_COLORS = [
    '#3f6a64', '#b28c50', '#5a9a94', '#7a6a40',
    '#2d524d', '#d4b870', '#4a7a74', '#9a8050',
];
const VEHICLE_COLORS_DARK = [
    '#5fd4c4', '#d4a85a', '#4ab8a8', '#c0a060',
    '#38a898', '#e8c080', '#60c0b0', '#b09050',
];

// Warning colors for under-charged vehicles
const WARN_COLOR = '#e65100'; // deep orange
const DANGER_COLOR = '#c62828'; // red

const ROW_HEIGHT = 40;
const LABEL_WIDTH = 160;

/**
 * Format fractional hour to "H:MM AM/PM" (e.g. 13.5 → "1:30 PM", 0.083 → "12:05 AM")
 */
function formatHour(h) {
    const wrapped = ((h % 24) + 24) % 24;
    const hours = Math.floor(wrapped);
    const minutes = Math.round((wrapped - hours) * 60);
    const period = hours < 12 ? 'AM' : 'PM';
    let displayHour = hours % 12;
    if (displayHour === 0) displayHour = 12;
    return minutes === 0
        ? `${displayHour} ${period}`
        : `${displayHour}:${String(minutes).padStart(2, '0')} ${period}`;
}

/**
 * Determine charge status for a session
 * Returns: 'ok' | 'warning' | 'danger'
 */
function getChargeStatus(session) {
    const minSoc = session.min_departure_soc;
    if (minSoc === undefined || minSoc === null) return 'ok';
    if (session.final_soc >= minSoc) return 'ok';
    // Below min but has some charge
    if (session.final_soc >= minSoc * 0.5) return 'warning';
    // Severely under-charged
    return 'danger';
}

/**
 * Build tooltip content for a vehicle session
 */
function SessionTooltip({ session }) {
    const status = getChargeStatus(session);
    const minSoc = session.min_departure_soc;
    const deficit = minSoc != null ? Math.max(0, minSoc - session.final_soc) : 0;

    return (
        <Box sx={{ p: 0.5, maxWidth: 260 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {session.vehicle_group_name || session.vehicle_id}
            </Typography>
            <Typography variant="caption" display="block">
                Charger: {session.charger_id !== null && session.charger_id !== undefined ? `#${session.charger_id + 1}` : 'Unassigned'}
            </Typography>
            <Typography variant="caption" display="block">
                Arrival: {formatHour(session.arrival_hour)} at {session.initial_soc}% SOC
            </Typography>
            {session.charge_start_hour != null && session.charge_end_hour != null && (
                <Typography variant="caption" display="block">
                    Charging: {formatHour(session.charge_start_hour)} → {formatHour(session.charge_end_hour)}
                </Typography>
            )}
            <Typography variant="caption" display="block">
                Departure: {formatHour(session.departure_hour)} at {session.final_soc}% SOC
            </Typography>
            {minSoc != null && (
                <Typography
                    variant="caption"
                    display="block"
                    sx={{
                        fontWeight: 600,
                        color: status === 'ok' ? '#4caf50' : status === 'warning' ? WARN_COLOR : DANGER_COLOR,
                    }}
                >
                    Route requires: {minSoc}% SOC
                    {status !== 'ok' && ` (short by ${Math.round(deficit)}%)`}
                </Typography>
            )}
            {session.daily_miles != null && (
                <Typography variant="caption" display="block">
                    Daily route: {session.daily_miles} miles
                </Typography>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)', pt: 0.5 }}>
                Energy: {session.energy_delivered_kwh} kWh &middot; Avg Rate: {session.avg_charging_rate_kw} kW
            </Typography>
            {session.battery_kwh && (
                <Typography variant="caption" display="block">
                    Battery: {session.battery_kwh} kWh
                </Typography>
            )}
        </Box>
    );
}

/**
 * ChargingScheduleChart Component
 */
function ChargingScheduleChart({
    vehicleSessions = [],
    repDaySoc = null,
    chargers = [],
    fleet = [],
    height = 400,
}) {
    const { isDark } = useThemeMode();
    const colors = isDark ? VEHICLE_COLORS_DARK : VEHICLE_COLORS;

    // Determine if any session wraps overnight (charge window crosses midnight)
    const hasOvernight = useMemo(
        () => vehicleSessions.some(s => {
            const start = s.charge_start_hour ?? s.arrival_hour;
            const end = s.charge_end_hour ?? s.departure_hour;
            return start > end;
        }),
        [vehicleSessions]
    );

    // Axis: 0–24 for same-day, 0–30 for overnight
    const axisHours = hasOvernight ? 30 : 24;

    // Build charger rows from sessions
    const { chargerRows, unassigned } = useMemo(() => {
        const byCharger = {};
        const noCharger = [];

        vehicleSessions.forEach(session => {
            if (session.charger_id === null || session.charger_id === undefined) {
                noCharger.push(session);
            } else {
                const key = session.charger_id;
                if (!byCharger[key]) byCharger[key] = [];
                byCharger[key].push(session);
            }
        });

        const sortedKeys = Object.keys(byCharger).sort((a, b) => Number(a) - Number(b));
        const rows = sortedKeys.map(key => {
            const idx = Number(key);
            const charger = chargers[idx];
            const label = charger
                ? `Charger ${idx + 1} (${charger.power_kw || charger.max_power_kw || '?'} kW${charger.connector_type ? ` ${charger.connector_type}` : ''})`
                : `Charger ${idx + 1}`;
            return { label, sessions: byCharger[key], chargerId: idx };
        });

        return { chargerRows: rows, unassigned: noCharger };
    }, [vehicleSessions, chargers]);

    // Hour tick marks for x-axis
    const hourTicks = useMemo(() => {
        const ticks = [];
        for (let h = 0; h <= axisHours; h += 3) {
            ticks.push(h);
        }
        return ticks;
    }, [axisHours]);

    // All rows including unassigned
    const allRows = useMemo(() => {
        const rows = [...chargerRows];
        if (unassigned.length > 0) {
            rows.push({ label: 'Unassigned', sessions: unassigned, chargerId: null });
        }
        return rows;
    }, [chargerRows, unassigned]);

    // Count under-charged vehicles for summary banner
    const { underCharged, totalWithRoute } = useMemo(() => {
        let under = 0;
        let total = 0;
        vehicleSessions.forEach(s => {
            if (s.min_departure_soc != null) {
                total++;
                if (s.final_soc < s.min_departure_soc) under++;
            }
        });
        return { underCharged: under, totalWithRoute: total };
    }, [vehicleSessions]);

    if (vehicleSessions.length === 0) {
        return (
            <SectionCard title="Charging Schedule" icon={<ChargerIcon />}>
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography color="text.secondary">
                        Run a simulation to see the charging schedule.
                    </Typography>
                </Box>
            </SectionCard>
        );
    }

    const getBlockPosition = (session) => {
        // Use actual charging window if available, otherwise full presence window
        let start = session.charge_start_hour ?? session.arrival_hour;
        let end = session.charge_end_hour ?? session.departure_hour;
        if (end <= start) end += 24;
        const left = (start / axisHours) * 100;
        const width = ((end - start) / axisHours) * 100;
        return { left: `${left}%`, width: `${Math.max(width, 1)}%` };
    };

    const getBlockGradient = (session, baseColor) => {
        if (!repDaySoc) return baseColor;
        const groupIdx = session.group_index;
        const socKey = groupIdx !== undefined ? `vehicle_${groupIdx}` : session.vehicle_id;
        const soc = repDaySoc[socKey];
        if (!soc || soc.length < 96) return baseColor;

        const startHour = session.charge_start_hour ?? session.arrival_hour;
        const endHour = session.charge_end_hour ?? session.departure_hour;
        const arrInterval = Math.round(startHour * 4) % 96;
        let depInterval = Math.round(endHour * 4) % 96;
        if (endHour <= startHour) depInterval += 96;

        const samples = 4;
        const opacities = [];
        for (let s = 0; s < samples; s++) {
            const t = arrInterval + ((depInterval - arrInterval) * s) / (samples - 1);
            const idx = Math.round(t) % 96;
            const nextIdx = (idx + 1) % 96;
            const delta = Math.max(0, (soc[nextIdx] || soc[idx]) - soc[idx]);
            opacities.push(0.4 + Math.min(delta / 10, 1) * 0.6);
        }

        const stops = opacities.map((op, i) => {
            const pct = (i / (samples - 1)) * 100;
            return `${alpha(baseColor, op)} ${pct}%`;
        });

        return `linear-gradient(to right, ${stops.join(', ')})`;
    };

    /**
     * Get block color based on charge status
     */
    const getBlockColor = (session, normalColor) => {
        const status = getChargeStatus(session);
        if (status === 'danger') return isDark ? '#ef5350' : DANGER_COLOR;
        if (status === 'warning') return isDark ? '#ff9800' : WARN_COLOR;
        return normalColor;
    };

    const totalRows = allRows.length;
    const chartHeight = Math.min(totalRows * (ROW_HEIGHT + 2) + 40, height);

    return (
        <SectionCard title="Charging Schedule" icon={<ChargerIcon />}>
            {/* Warning banner when vehicles are under-charged */}
            {underCharged > 0 && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    mb: 2,
                    borderRadius: 1,
                    bgcolor: alpha(DANGER_COLOR, isDark ? 0.15 : 0.08),
                    border: `1px solid ${alpha(DANGER_COLOR, 0.4)}`,
                }}>
                    <WarningIcon sx={{ color: isDark ? '#ef5350' : DANGER_COLOR, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: isDark ? '#ef5350' : DANGER_COLOR, fontWeight: 600 }}>
                        {underCharged} of {totalWithRoute} vehicle{totalWithRoute !== 1 ? 's' : ''} won&apos;t have enough charge for their routes
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
                        Add chargers or increase power
                    </Typography>
                </Box>
            )}

            <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: LABEL_WIDTH + 400 }}>
                    {/* Hour labels */}
                    <Box sx={{ position: 'relative', height: 18, ml: `${LABEL_WIDTH}px`, mr: 1, mb: 0.5 }}>
                        {hourTicks.map(h => (
                            <Typography
                                key={h}
                                variant="caption"
                                sx={{
                                    position: 'absolute',
                                    left: `${(h / axisHours) * 100}%`,
                                    color: 'text.secondary',
                                    fontSize: '0.65rem',
                                    transform: 'translateX(-50%)',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {formatHour(h)}
                            </Typography>
                        ))}
                    </Box>

                    {/* Scrollable rows */}
                    <Box
                        sx={{
                            position: 'relative',
                            mt: 2.5,
                            maxHeight: chartHeight,
                            overflowY: totalRows > 12 ? 'auto' : 'visible',
                        }}
                    >
                        {allRows.map((row) => (
                            <Box
                                key={row.label}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: ROW_HEIGHT,
                                    mb: 0.25,
                                }}
                            >
                                {/* Row label */}
                                <Typography
                                    variant="caption"
                                    sx={{
                                        width: LABEL_WIDTH,
                                        flexShrink: 0,
                                        pr: 1,
                                        textAlign: 'right',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontWeight: 500,
                                        color: row.chargerId === null && row.label === 'Unassigned'
                                            ? 'text.disabled'
                                            : 'text.secondary',
                                        fontSize: '0.7rem',
                                    }}
                                >
                                    {row.label}
                                </Typography>

                                {/* Timeline bar — fills remaining width */}
                                <Box
                                    sx={{
                                        position: 'relative',
                                        flex: 1,
                                        height: ROW_HEIGHT - 4,
                                        bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                                        borderRadius: 0.5,
                                        overflow: 'hidden',
                                    }}
                                >
                                    {row.sessions.map((session, si) => {
                                        const uniqueIdx = (session.group_index ?? 0) * 8 + (session.instance_index ?? si);
                                        const normalColor = colors[uniqueIdx % colors.length];
                                        const isUnassigned = row.chargerId === null && row.label === 'Unassigned';
                                        const status = getChargeStatus(session);
                                        const blockColor = isUnassigned
                                            ? (isDark ? '#888' : '#999')
                                            : getBlockColor(session, normalColor);
                                        const pos = getBlockPosition(session);
                                        const bg = (isUnassigned || status !== 'ok')
                                            ? blockColor
                                            : getBlockGradient(session, blockColor);

                                        const vehicleName = session.vehicle_group_name || session.vehicle_id;
                                        const socLabel = `${Math.round(session.initial_soc)}%→${Math.round(session.final_soc)}%`;
                                        const showWarning = status !== 'ok' && !isUnassigned;

                                        return (
                                            <Tooltip
                                                key={session.vehicle_id || si}
                                                title={<SessionTooltip session={session} />}
                                                arrow
                                                placement="top"
                                            >
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        left: pos.left,
                                                        width: pos.width,
                                                        top: 2,
                                                        bottom: 2,
                                                        background: bg,
                                                        borderRadius: 0.5,
                                                        border: `1px solid ${alpha(blockColor, 0.8)}`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 0.5,
                                                        gap: 0.3,
                                                        overflow: 'hidden',
                                                        cursor: 'pointer',
                                                        opacity: isUnassigned ? 0.5 : 1,
                                                        transition: 'opacity 0.15s',
                                                        '&:hover': { opacity: 1 },
                                                    }}
                                                >
                                                    {showWarning && (
                                                        <WarningIcon sx={{
                                                            fontSize: 12,
                                                            color: '#fff',
                                                            flexShrink: 0,
                                                            filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.3))',
                                                        }} />
                                                    )}
                                                    <Typography
                                                        variant="caption"
                                                        noWrap
                                                        sx={{
                                                            fontSize: '0.6rem',
                                                            fontWeight: 600,
                                                            color: (showWarning || isDark) ? 'rgba(255,255,255,0.95)' : '#fff',
                                                            textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {vehicleName} {socLabel}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        );
                                    })}
                                </Box>
                            </Box>
                        ))}

                        {/* Vertical hour gridlines — positioned within the flex timeline area */}
                        {hourTicks.filter(h => h > 0 && h < axisHours).map(h => (
                            <Box
                                key={`grid-${h}`}
                                sx={{
                                    position: 'absolute',
                                    left: `calc(${LABEL_WIDTH}px + (100% - ${LABEL_WIDTH}px) * ${h / axisHours})`,
                                    top: 0,
                                    bottom: 0,
                                    width: '1px',
                                    bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                                    pointerEvents: 'none',
                                }}
                            />
                        ))}
                    </Box>

                    {/* Legend */}
                    <Box sx={{ display: 'flex', gap: 2, mt: 2, ml: `${LABEL_WIDTH}px`, flexWrap: 'wrap', alignItems: 'center' }}>
                        {fleet.map((v, i) => {
                            const color = colors[i % colors.length];
                            const name = v.name || v.route_name || v.vehicle_type || `Vehicle ${i}`;
                            return (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                        width: 14,
                                        height: 14,
                                        bgcolor: color,
                                        borderRadius: 0.5,
                                        border: `1px solid ${alpha(color, 0.7)}`,
                                    }} />
                                    <Typography variant="caption" color="text.secondary">
                                        {name}
                                    </Typography>
                                </Box>
                            );
                        })}
                        {/* Warning legend items */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                            <Box sx={{
                                width: 14,
                                height: 14,
                                bgcolor: isDark ? '#ff9800' : WARN_COLOR,
                                borderRadius: 0.5,
                            }} />
                            <Typography variant="caption" color="text.secondary">Low charge</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{
                                width: 14,
                                height: 14,
                                bgcolor: isDark ? '#ef5350' : DANGER_COLOR,
                                borderRadius: 0.5,
                            }} />
                            <Typography variant="caption" color="text.secondary">Insufficient for route</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </SectionCard>
    );
}

export default ChargingScheduleChart;
